import React, { useEffect, useState } from 'react';
import { ImagePlus, Trash2, X, Upload, Crop } from 'lucide-react';
import ReactCrop, { Crop as CropType } from 'react-image-crop';
import { galleryClass, GalleryPictureContent } from '../../../services/Gallery';
import Sidebar from '../../../component/includes/sidebar';

export default function DashboardGallery() {
  const [images, setImages] = useState<GalleryPictureContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<CropType>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5,
  });
  const [isCropping, setIsCropping] = useState(false);
  const [imageRef, setImageRef] = useState<HTMLImageElement | null>(null);

  const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const data = await galleryClass.getAllGalleryPictures();
      setImages(data);
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to load gallery images');
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return 'Please select a valid image file (PNG, JPEG, JPG, or WebP)';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size should be less than 5MB';
    }
    return null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const validationError = validateFile(file);
      
      if (validationError) {
        setError(validationError);
        return;
      }

      setError(null);
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setIsCropping(false); // reset cropping saat upload baru
    }
  };

  const getCroppedImg = (image: HTMLImageElement, crop: CropType): Promise<Blob> => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width!;
    canvas.height = crop.height!;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2d context');

    ctx.drawImage(
      image,
      crop.x! * scaleX,
      crop.y! * scaleY,
      crop.width! * scaleX,
      crop.height! * scaleY,
      0,
      0,
      crop.width!,
      crop.height!
    );

    return new Promise((resolve) => {
      const fileType = selectedFile?.type || 'image/jpeg';
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
      }, fileType, 1);
    });
  };

  const handleUpload = async () => {
    if (!selectedFile || !imageRef) return;

    try {
      let fileToUpload = selectedFile;

      if (isCropping && imageRef) {
        const croppedBlob = await getCroppedImg(imageRef, crop);
        fileToUpload = new File([croppedBlob], selectedFile.name, {
          type: selectedFile.type,
          lastModified: Date.now(),
        });
      }

      await galleryClass.createGalleryPicture({ image: fileToUpload });
      resetUpload();
      fetchGalleryImages();
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to upload image');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await galleryClass.deleteGalleryPicture(id);
      fetchGalleryImages();
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to delete image');
    }
  };

  const getImageUrl = (imagePath: string) => {
    return `${process.env.REACT_APP_API_URL}${imagePath}`;
  };

  const resetUpload = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsCropping(false);
    setError(null);
    setCrop({ unit: '%', width: 90, height: 90, x: 5, y: 5 });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="flex flex-col md:flex-row">
        <Sidebar />

        <div className="flex-1 p-4 md:p-6 mt-10">
          <div className="max-w-7xl mx-auto">
            {/* Header seperti di gambar */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-8 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <ImagePlus size={24} className="text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Gallery Dashboard</h1>
                  <p className="text-sm text-gray-500">Manage your gallery images</p>
                </div>
              </div>

              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-medium transition-all active:scale-95"
              >
                <ImagePlus size={20} />
                Add Image
              </button>
            </div>

            {/* Gallery Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {images.map((image) => (
                <div
                  key={image.id}
                  className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <img
                    src={getImageUrl(image.image)}
                    alt="Gallery"
                    className="w-full aspect-video object-cover"
                  />
                  <button
                    onClick={() => handleDelete(image.id)}
                    className="absolute top-3 right-3 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all shadow-md"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>

            {/* Total Images */}
            <div className="mt-10 flex justify-center">
              <div className="bg-white px-8 py-3 rounded-2xl shadow-sm text-gray-700 font-medium">
                Total Images: <span className="font-semibold text-blue-600">{images.length}</span>
              </div>
            </div>

            {images.length === 0 && (
              <div className="text-center py-20 text-gray-400">
                No images in the gallery yet
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Upload Image</h2>
              <button onClick={resetUpload} className="text-gray-400 hover:text-gray-600">
                <X size={28} />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-xl text-sm">
                {error}
              </div>
            )}

            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center mb-6">
              {!previewUrl ? (
                <>
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="flex flex-col items-center cursor-pointer"
                  >
                    <ImagePlus size={64} className="text-gray-300 mb-4" />
                    <p className="text-gray-600 font-medium">Click to upload image</p>
                    <p className="text-xs text-gray-400 mt-2">
                      PNG, JPEG, JPG, WebP • Max 5MB
                    </p>
                  </label>
                </>
              ) : (
                <div className="relative">
                  {isCropping ? (
                    <ReactCrop crop={crop} onChange={(c) => setCrop(c)} aspect={undefined}>
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="max-h-[360px] w-full object-contain rounded-xl"
                        onLoad={(e) => setImageRef(e.currentTarget)}
                      />
                    </ReactCrop>
                  ) : (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="max-h-[360px] w-full object-contain rounded-xl"
                      onLoad={(e) => setImageRef(e.currentTarget)}
                    />
                  )}
                </div>
              )}
            </div>

            {previewUrl && (
              <div className="flex justify-center mb-6">
                <button
                  onClick={() => setIsCropping(!isCropping)}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  <Crop size={20} />
                  {isCropping ? 'Cancel Crop' : 'Crop Image'}
                </button>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={resetUpload}
                className="flex-1 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile}
                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl font-medium transition flex items-center justify-center gap-2"
              >
                <Upload size={20} />
                Upload Image
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}